/**
 * SnapTradePortal — web implementation
 *
 * Renders the SnapTrade connection portal in an iframe modal using the
 * official snaptrade-react package. The portal stays within the app;
 * no popup window is opened.
 */

import { lazy, Suspense, type ComponentType } from 'react';

interface Props {
  loginLink: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (authorizationId: string) => void;
  onError?: (detail: string) => void;
}

type SnapTradeReactProps = {
  loginLink: string;
  isOpen: boolean;
  close: () => void;
  onSuccess: (authorizationId: string) => void;
  onError: (err: { detail: string }) => void;
  onExit: () => void;
};

const SnapTradeReact = lazy(async () => {
  const mod = await import('snaptrade-react');
  return {
    default: mod.SnapTradeReact as ComponentType<SnapTradeReactProps>,
  };
});

export function SnapTradePortal({ loginLink, isOpen, onClose, onSuccess, onError }: Props) {
  if (!loginLink || !isOpen) return null;

  return (
    <Suspense fallback={null}>
      <SnapTradeReact
        loginLink={loginLink}
        isOpen={isOpen}
        close={onClose}
        onSuccess={onSuccess}
        onError={(err) => onError?.(err.detail)}
        onExit={onClose}
      />
    </Suspense>
  );
}
